/**
 * Параметры запуска мини-аппа, прочитанные ИЗ АДРЕСА — без Telegram SDK.
 *
 * Открывая мини-апп, клиент Telegram сам дописывает их в адрес:
 *   #tgWebAppData=<initData>&tgWebAppVersion=7.10&tgWebAppPlatform=ios&…
 * `tgWebAppData` — это и есть строка initData, которую проверяет бэкенд
 * (modules/account/services/telegram/TelegramInitDataValidator): та же
 * подпись, тот же порядок полей. Никакого дополнительного источника правды
 * здесь нет — мы читаем ровно то, что читает сам SDK.
 *
 * Зачем отдельно от SDK: вход не имеет права ждать загрузки скрипта. Пока
 * initData добывался только через `window.Telegram.WebApp`, вход в России не
 * происходил вообще — скрипт ехал с заблокированного telegram.org, не
 * доезжал, и авторизация молча пропускалась (человек видел лендинг с кнопкой
 * «Войти» внутри мини-аппа). SDK по-прежнему нужен — для ready()/expand() и
 * нативных кнопок, — но он грузится своим чередом и вход больше не держит.
 *
 * Значение кэшируется в sessionStorage: роутер после первой навигации хэш из
 * адреса убирает, а экран /tma-init и повторная попытка входа читают его уже
 * после этого.
 */

/** Свой ключ: пустая строка хранит отказ нового запуска, в том числе после reload. */
export const TMA_INIT_DATA_KEY = 'igra_tma_init_data'

/** Имя параметра запуска, в котором клиент Telegram приносит initData. */
const LAUNCH_PARAM = 'tgWebAppData'

// Не разрешать SDK подставить старые данные после явно некорректного запуска.
let rejectedLaunch = false

/**
 * Достать значение `tgWebAppData` из строки параметров.
 *
 * Разбираем вручную, а не через URLSearchParams: тот превращает «+» в пробел,
 * и подпись initData от такой любезности перестала бы сходиться.
 */
function pickLaunchParam(raw: string): string | null | undefined {
    const source = raw.replace(/^[#?]/, '')
    if (source === '') return undefined

    for (const pair of source.split('&')) {
        if (pair === LAUNCH_PARAM) return null

        const eq = pair.indexOf('=')
        if (eq === -1) continue
        if (pair.slice(0, eq) !== LAUNCH_PARAM) continue

        const value = pair.slice(eq + 1)
        if (value === '') return null

        try {
            return decodeURIComponent(value)
        } catch {
            // Битый percent-encoding: пустой строкой «авторизоваться» нельзя,
            // честнее считать, что initData нет.
            return null
        }
    }

    return undefined
}

function cacheRead(): string | null {
    try {
        return sessionStorage.getItem(TMA_INIT_DATA_KEY)
    } catch {
        return null
    }
}

function cacheWrite(initData: string | null): void {
    try {
        // Не удалять ключ при отказе: иначе reload снова разрешит старый SDK-кэш.
        sessionStorage.setItem(TMA_INIT_DATA_KEY, initData ?? '')
    } catch {
        // sessionStorage недоступен (приватный режим) — вход от кэша не зависит
    }
}

/**
 * initData этого запуска: сначала из адреса, иначе из кэша сессии.
 *
 * `null` означает «мы не в мини-аппе или клиент параметров не передал» —
 * законный ответ, а не ошибка.
 */
export function readTelegramInitData(sdkInitData: string | null = null): string | null {
    if (typeof window === 'undefined') return null

    const fromHash = pickLaunchParam(window.location.hash)
    const fromLocation = fromHash !== undefined ? fromHash : pickLaunchParam(window.location.search)

    // Явно переданные, но битые данные нового запуска не заменяем старым аккаунтом.
    if (fromLocation !== undefined) {
        rejectedLaunch = fromLocation === null
        cacheWrite(fromLocation)

        return fromLocation
    }

    if (rejectedLaunch) return null

    const cached = cacheRead()
    return cached === '' ? null : cached ?? (sdkInitData || null)
}

/** Есть ли в адресе параметры запуска Telegram — без разбора значения. */
export function hasLaunchParamsInLocation(): boolean {
    if (typeof window === 'undefined') return false

    return window.location.href.includes('tgWebApp')
}
